// The plan rebuild after a real profile change (P0 2026-10-30). The SERVER decides whether a save changed the
// training days / their number / the weekly volume / the injury state (a durable "rebuild required" with a request
// id); this helper only asks before a days / volume change, starts the rebuild for the current request and reports
// the outcome. GET /weekly-plan/rebuild-status and POST /weekly-plan/rebuild accept the athlete or their coach
// (user_id = the athlete), so the profile screen, "מוכן לחזור", the plan screen and the coach dashboard share it.
import authFetch from './authFetch';

const API = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// Owner wording (2026-10-31): any real change of a plan input rebuilds the plan and replaces earlier changes.
export const REBUILD_WARNING = 'שינוי הפרופיל יבנה מחדש את תוכנית האימונים ויחליף שינויים קודמים שביצעת באימונים.';
export const REBUILD_CONFIRM_LABEL = 'עדכן ובנה מחדש';

const DAY_ORDER = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const days = (s) => [...new Set(String(s || '').split(/[\s,]+/).filter((d) => DAY_ORDER.includes(d)))]
  .sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)).join(' ');
const num = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
const injury = (v) => (String(v || '').trim().toLowerCase() || 'ok');
const txt = (v) => String(v == null ? '' : v).trim().toLowerCase();
// the plan inputs besides days / volume / injury (server: plan_rebuild.WATCHED); strength is not a plan input
const PLAN_TEXT_FIELDS = ['goal_distance', 'goal_time', 'goal_date', 'goal_race', 'level', 'preferred_long_run_day'];

/** What a save changes, compared with the values the plan was last saved with — ONLY to decide whether to ask
 *  before the save. Whether the plan is rebuilt is the SERVER's decision (rebuild-status after the save).
 *  settings -> the warning before the save; injury -> the plan follows it without a question. */
export function planRebuildChange(saved, current) {
  const a = saved || {};
  const b = current || {};
  return {
    settings: days(a.preferred_days) !== days(b.preferred_days) || num(a.avg_weekly_km) !== num(b.avg_weekly_km)
      || PLAN_TEXT_FIELDS.some((k) => txt(a[k]) !== txt(b[k])),
    injury: injury(a.injury_state) !== injury(b.injury_state)
      || String(a.injury_discipline || '') !== String(b.injury_discipline || ''),
  };
}

export async function getRebuildStatus(userId) {
  const res = await authFetch(`${API}/weekly-plan/rebuild-status?user_id=${encodeURIComponent(userId)}`);
  if (!res.ok) throw new Error(`rebuild-status ${res.status}`);
  return res.json();
}

const MSG = {
  settings_ok: 'התכנית עודכנה לפי הפרופיל — ארבעת השבועות הקרובים נבנו מחדש.',
  injury_ok: 'התכנית הותאמה למצב הפציעה — ארבעת השבועות הקרובים נבנו מחדש.',
  settings_failed: 'עדכון התכנית נכשל. התכנית הקודמת נשארה ואינה מעודכנת לשינוי בפרופיל — אפשר לנסות שוב.',
  injury_failed: 'עדכון התכנית נכשל. התכנית הקודמת נשארה ואינה מעודכנת למצב הפציעה החדש — אפשר לנסות שוב.',
  needs_attention: 'עדכון קודם של התכנית לא הושלם ונדרש טיפול. פנה לתמיכה — התכנית לא תשתנה עד אז.',
  running: 'העדכון עדיין רץ — הסטטוס יופיע במסך התכנית.',
};

const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });
const fire = (name, detail) => { try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch (_e) { /* no window */ } };

/** An earlier rebuild is still running (a second save while it runs): wait until it ends -> the latest status. */
async function waitForNoJob(userId, pollMs, maxPolls) {
  for (let i = 0; i < maxPolls; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await wait(pollMs);
    try {
      // eslint-disable-next-line no-await-in-loop
      const s = await getRebuildStatus(userId);
      if (!s.job) return s;
    } catch (_e) { /* transient — keep waiting */ }
  }
  return null;
}

/** Start the rebuild the server requires (if any) and wait for its outcome.
 *  -> { ok, kind: 'none'|'done'|'failed'|'safety_hold'|'needs_attention'|'running', message } */
export async function runPlanRebuild(userId, { reason = 'settings', pollMs = 3000, maxPolls = 120 } = {}) {
  let st = await getRebuildStatus(userId);
  if (!st.required) return { ok: true, kind: 'none', message: '' };
  if (st.needs_attention) return { ok: false, kind: 'needs_attention', message: MSG.needs_attention };
  let res = null;
  let body = {};
  for (let attempt = 0; attempt < 3; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    res = await authFetch(`${API}/weekly-plan/rebuild?user_id=${encodeURIComponent(userId)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rid: st.rid }) });
    // eslint-disable-next-line no-await-in-loop
    body = await res.json().catch(() => ({}));
    if (res.status === 409 && body.error === 'stale_rebuild_request') {   // a newer save — go with the latest
      // eslint-disable-next-line no-await-in-loop
      st = await getRebuildStatus(userId);
      if (!st.required) return { ok: true, kind: 'none', message: '' };
      continue;                                                        // eslint-disable-line no-continue
    }
    if (res.status === 409 && body.error === 'job_in_progress' && attempt < 2) {
      // the rebuild of an earlier save is running: it stops for this newer request — then start the latest one
      // eslint-disable-next-line no-await-in-loop
      st = await waitForNoJob(userId, pollMs, maxPolls);
      if (!st) return { ok: false, kind: 'running', message: MSG.running };
      if (!st.required) return { ok: true, kind: 'none', message: '' };
      if (st.needs_attention) return { ok: false, kind: 'needs_attention', message: MSG.needs_attention };
      continue;                                                        // eslint-disable-line no-continue
    }
    break;
  }
  if (res.status === 409) {
    if (body.error === 'safety_hold') return { ok: false, kind: 'safety_hold', message: body.message };
    if (body.error === 'needs_attention') return { ok: false, kind: 'needs_attention', message: MSG.needs_attention };
    if (body.error === 'no_pending') return { ok: true, kind: 'none', message: '' };
    return { ok: false, kind: 'failed', message: body.message || MSG[`${reason}_failed`] };
  }
  if (!res.ok || !body.job_id) return { ok: false, kind: 'failed', message: MSG[`${reason}_failed`] };
  fire('plan-regen-start', { changedFields: ['עדכון פרופיל'] });
  for (let i = 0; i < maxPolls; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await wait(pollMs);
    let s;
    try {
      // eslint-disable-next-line no-await-in-loop
      s = await getRebuildStatus(userId);
    } catch (_e) { continue; }                                         // eslint-disable-line no-continue
    const last = s.last_job || {};
    if (s.job || last.job_id !== body.job_id) continue;                // eslint-disable-line no-continue
    if (last.status !== 'completed' && last.reason === 'profile_changed') {
      // superseded by a newer save, which starts its own rebuild: this one ends quietly (nothing was changed)
      fire('plan-regen-done', { dismiss: true });
      return { ok: true, kind: 'none', message: '' };
    }
    if (last.status === 'completed') {
      fire('plan-regen-done', { success: true });
      fire('plansUpdated', { source: 'profile-rebuild' });
      return { ok: true, kind: 'done', message: MSG[`${reason}_ok`] };
    }
    fire('plan-regen-done', { success: false, error: last.reason || last.error || 'failed' });
    if (s.needs_attention || last.rollback === 'failed') {
      return { ok: false, kind: 'needs_attention', message: MSG.needs_attention };
    }
    if (last.reason === 'safety_hold' || s.safety_hold) return { ok: false, kind: 'safety_hold', message: MSG[`${reason}_failed`] };
    return { ok: false, kind: 'failed', message: MSG[`${reason}_failed`] };
  }
  fire('plan-regen-done', { dismiss: true });                          // still running: no success claimed
  return { ok: false, kind: 'running', message: MSG.running };
}
